/**
 * Effect-native query atom.
 *
 * run 返回 `Effect<A, E>`，错误类型 E 原样保留在 AsyncResult；禁用查询按 key
 * 族返回共享 Initial atom。Promise 输入桥已删除。
 */

import { Effect } from "effect";
import * as Atom from "effect/unstable/reactivity/Atom";
import * as AsyncResult from "effect/unstable/reactivity/AsyncResult";
import type { QueryAtomOptions } from "./query-atom";

export type QueryAtomEffectSelector<A, E, K extends readonly unknown[]> = (
  key: K,
  enabled?: boolean,
) => Atom.Atom<AsyncResult.AsyncResult<A, E>>;

/**
 * 从 Effect 程序创建 memoized query-atom family。
 *
 * 读取 atom 即运行 Effect；staleTime / reactivityKeys 语义与 queryAtom 相同。
 * 错误通道 E 保留：UI 可穷尽区分领域错误而非 cast 成 Error。
 * R（如 RPC 的 Scope）由 reactivity runtime 在求值期提供。
 */
export function queryAtomEffect<A, E, R, K extends readonly unknown[]>(
  run: (key: K) => Effect.Effect<A, E, R>,
  options?: QueryAtomOptions,
): QueryAtomEffectSelector<A, E, K> {
  const family = Atom.family((key: K) => {
    const atom = Atom.make(run(key) as Effect.Effect<A, E>).pipe(
      Atom.swr({ staleTime: options?.staleTime ?? 0 }),
    );
    return withReactivityOptions(atom, options, key);
  });
  const disabled = Atom.make(AsyncResult.initial<A, E>());
  return (key, enabled = true) => (enabled ? family(key) : disabled);
}

function withReactivityOptions<A, E, K extends readonly unknown[]>(
  atom: Atom.Atom<AsyncResult.AsyncResult<A, E>>,
  options: QueryAtomOptions | undefined,
  key: K,
) {
  return options?.reactivityKeys
    ? atom.pipe(
        Atom.withReactivity(
          typeof options.reactivityKeys === "function"
            ? options.reactivityKeys(key)
            : options.reactivityKeys,
        ) as (a: typeof atom) => typeof atom,
      )
    : atom;
}
